<template>
  <!-- Mobile Toggle -->
  <div
    class="navigation-menu__mobile-header d-lg-none d-flex align-items-center p-3"
    :class="{
      'navigation-menu__mobile-header--development': !isProduction,
      'navigation-menu__mobile-header--production': isProduction,
    }"
  >
    <button
      class="btn btn-outline-secondary text-white"
      type="button"
      data-bs-toggle="offcanvas"
      data-bs-target="#sidebarMobile"
      aria-controls="sidebarMobile"
    >
      <i class="bi bi-list"></i>
    </button>

    <router-link to="/" class="mx-3 text-decoration-none text-white">{{ APP_NAME }}</router-link>
  </div>

  <NavigationDesktop :display-name="displayName" :is-production="isProduction" />

  <!-- Mobile -->
  <div
    id="sidebarMobile"
    class="offcanvas offcanvas-start d-lg-none"
    tabindex="-1"
    aria-labelledby="sidebarLabel"
  >
    <div class="offcanvas-header d-flex align-items-center">
      <h5 class="mb-0">
        <router-link to="/" class="text-decoration-none text-white">
          {{ APP_NAME }}
        </router-link>
      </h5>

      <button id="mobile-menu-close-cta" class="btn-close" data-bs-dismiss="offcanvas"></button>
    </div>

    <div class="offcanvas-body d-flex flex-column">
      <NavigationSearch />

      <NavigationSideBarTitle title="General" />

      <template v-for="link in SIDEBAR_GENERAL_LINKS" :key="link.route">
        <NavigationMainLinks :link-info="link" />
      </template>

      <div class="sidebar-divider"></div>

      <NavigationSideBarTitle title="Configuration" />

      <template v-for="link in SIDEBAR_CONFIGURATION_LINKS" :key="link.route">
        <NavigationMainLinks :link-info="link" />
      </template>

      <div class="sidebar-divider"></div>

      <NavigationSideBarTitle title="Diagnostics" />

      <template v-for="link in SIDEBAR_DIAGNOSTICS_LINKS" :key="link.route">
        <NavigationMainLinks :link-info="link" />
      </template>

      <div class="sidebar-divider"></div>

      <button class="btn btn-link text-decoration-none text-start text-white" @click="logout">
        <i class="bi bi-box-arrow-right me-2"></i>
        logout
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import {
  APP_NAME,
  SIDEBAR_CONFIGURATION_LINKS,
  SIDEBAR_DIAGNOSTICS_LINKS,
  SIDEBAR_GENERAL_LINKS,
} from '@/components/navigation/constants/NAVIGATION';
import NavigationDesktop from '@/components/navigation/NavigationDesktop.vue';
import NavigationMainLinks from '@/components/navigation/NavigationMainLinks.vue';
import NavigationSearch from '@/components/navigation/NavigationSearch.vue';
import NavigationSideBarTitle from '@/components/navigation/NavigationSideBarTitle.vue';
import { RouteNames } from '@/router/constants/routeName';
import { checkIfProduction } from '@/utils';

type Props = {
  displayName: string;
};

defineProps<Props>();

const router = useRouter();
const route = useRoute();

const isProduction = checkIfProduction();

const logout = () => {
  router.push({ name: RouteNames.Logout });
};

watch(
  () => route?.path,
  () => {
    const mobileMenuElement = document.querySelector('#sidebarMobile');
    const isMobileMenuOpen = mobileMenuElement?.classList.contains('show');

    // Not an elegant way to handle close using JS
    // ToDo: Refactor the same to use v-model and get rid of js based approach
    if (isMobileMenuOpen) {
      document.querySelector<HTMLElement>('#mobile-menu-close-cta')?.click();
    }
  },
);
</script>

<style lang="scss" scoped>
.navigation__display-name {
  max-width: 9.375rem;
  display: inline-block;
}

/* Divider */
.sidebar-divider {
  height: 0.0625rem;
  background: rgba(255, 255, 255, 0.08);
  margin: 0.75rem 0;
}

/* Mobile */
.offcanvas {
  background: linear-gradient(180deg, #2b1320, #1d1723, #14171d);
}

.offcanvas :deep(.sidebar-link) {
  color: #d7dce6;
}

.offcanvas :deep(.sidebar-link:hover) {
  color: white;
}

.offcanvas-body {
  height: 100%;
  min-height: 0;
}

.navigation-menu__logout-cta {
  background: linear-gradient(90deg, #9c314d, #702640);
  box-shadow: 0 0.625rem 1.375rem rgba(156, 49, 77, 0.35);
}

.navigation-menu__mobile-header {
  position: sticky;
  top: 0;
  left: 0;
}

.navigation-menu__mobile-header--development {
  background: linear-gradient(180deg, #612336 0%, #511e2b 35%, #301118 100%);
}

.navigation-menu__mobile-header--production {
  background: linear-gradient(180deg, #243763 0%, #1e2b51 35%, #15203b 100%);
}
</style>
